import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserSession } from '@/contexts/UserSessionContext';
import { retroalimentacionesService } from '@/services/api/retroalimentaciones.service';
import { getBusinessTodayISODate } from '@/shared/time/businessTime';
import { DIAS_MINIMOS_ENTRE_ENCUESTAS_NPS, FLUJOS_CAPTURA_RETROALIMENTACION, HABILITAR_NPS_AUTOMATICO } from '../constantes';
import { ModalRetroalimentacionObligatoria } from '../componentes/ModalRetroalimentacionObligatoria';
import { useRetroalimentacion } from '../hooks/useRetroalimentacion';
import { puedeMostrarEncuestaNps } from '../reglasFrecuencia';
import type { FlujoCapturaRetroalimentacion } from '../tipos';

const elegirFlujoAleatorio = (): FlujoCapturaRetroalimentacion =>
  FLUJOS_CAPTURA_RETROALIMENTACION[Math.floor(Math.random() * FLUJOS_CAPTURA_RETROALIMENTACION.length)];

const leerCacheMensual = (clave: string): boolean => {
  try {
    return window.sessionStorage.getItem(clave) === '1';
  } catch {
    return false;
  }
};

const guardarCacheMensual = (clave: string): void => {
  try {
    window.sessionStorage.setItem(clave, '1');
  } catch {
    return;
  }
};

export function ControladorRetroalimentacion() {
  const { panelAbierto, solicitarEncuestaNps, ultimaRespuestaNpsEn } = useRetroalimentacion();
  const { session } = useUserSession();
  const userId = session?.userId;
  const evaluadoRef = useRef(false);
  const [flujoObligatorio] = useState<FlujoCapturaRetroalimentacion>(elegirFlujoAleatorio);
  const [obligatoriaPendiente, setObligatoriaPendiente] = useState(false);
  const claveCache = userId
    ? `retroalimentacion_mensual:${userId}:${getBusinessTodayISODate().slice(0, 7)}`
    : null;

  useEffect(() => {
    if (!claveCache || leerCacheMensual(claveCache)) return;
    let cancelado = false;

    void retroalimentacionesService.getEstadoMensual()
      .then((respuesta) => {
        if (cancelado || !respuesta.ok || !respuesta.data) return;
        if (respuesta.data.obligatoria) {
          setObligatoriaPendiente(true);
        } else {
          guardarCacheMensual(claveCache);
        }
      })
      .catch(() => {});

    return () => { cancelado = true; };
  }, [claveCache]);

  const continuar = useCallback(() => {
    if (claveCache) guardarCacheMensual(claveCache);
    setObligatoriaPendiente(false);
  }, [claveCache]);

  useEffect(() => {
    if (evaluadoRef.current || panelAbierto) {
      return;
    }

    evaluadoRef.current = true;

    if (!puedeMostrarEncuestaNps({
      habilitado: HABILITAR_NPS_AUTOMATICO,
      ahora: new Date(),
      ultimaRespuestaNpsEn,
      diasMinimosEntreEncuestas: DIAS_MINIMOS_ENTRE_ENCUESTAS_NPS,
    })) {
      return;
    }

    solicitarEncuestaNps();
  }, [panelAbierto, solicitarEncuestaNps, ultimaRespuestaNpsEn]);

  if (!obligatoriaPendiente) {
    return null;
  }

  return (
    <ModalRetroalimentacionObligatoria
      flujo={flujoObligatorio}
      onContinuar={continuar}
    />
  );
}
